import { useQueryClient } from '@tanstack/react-query'
import { useRevenueSyncStore } from '../store/revenueSyncStore.js'
import { syncRevenueFromHubspot } from '../data/goalsApi.js'
import { timeAgo } from '../data/metaKeys.js'

/**
 * RevenueSyncBanner — small status bar shown above the Revenue pillar.
 *
 * Shows:
 *   - "Syncing from HubSpot…" while a sync is in flight
 *   - "Synced 2m ago" (+ totals) after a successful run
 *   - "Sync failed: <message>" if the latest attempt errored
 *
 * "Sync now" button forces an immediate sync, in addition to the
 * automatic 10-minute poll that runs from useRevenueAutoSync. The
 * store's `inFlight` lock prevents the two from clashing.
 */
export default function RevenueSyncBanner() {
  const queryClient = useQueryClient()
  const { status, lastSyncedAt, lastReceipt, error, runSync, inFlight } =
    useRevenueSyncStore()

  const onSyncNow = async () => {
    try {
      await runSync(syncRevenueFromHubspot)
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    } catch {
      // Store already captured the error; nothing to do here.
    }
  }

  // Style varies by status — keep the banner visually lightweight when
  // idle so it doesn't dominate the pillar view.
  const palette =
    status === 'error'
      ? { bg: '#fef2f2', border: '#fecaca', color: '#991b1b' }
      : status === 'syncing'
      ? { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8' }
      : { bg: '#f9fafb', border: 'var(--border)', color: 'var(--muted)' }

  const totals = lastReceipt?.totals
  const dealCount = lastReceipt?.dealsCountedQ2

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 12px',
        margin: '0 0 10px',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        fontSize: 11,
        color: palette.color,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontWeight: 700 }}>🔗 HubSpot sync</span>

      {status === 'syncing' && <span>Fetching Q2 closed-won deals…</span>}

      {status === 'error' && (
        <span style={{ fontWeight: 600 }}>
          Failed: {error}
        </span>
      )}

      {status === 'success' && lastSyncedAt && (
        <>
          <span>
            Synced {timeAgo(new Date(lastSyncedAt).getTime())}
            {typeof dealCount === 'number' ? ` · ${dealCount} Q2 deals` : ''}
          </span>
          {totals && (
            <span style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
              · total ${Math.round(totals.totalHsRev).toLocaleString()} (new-cust ${Math.round(totals.newCustRev).toLocaleString()} · design ${Math.round(totals.designRev).toLocaleString()} · TE ${Math.round(totals.teRev).toLocaleString()})
            </span>
          )}
        </>
      )}

      {status === 'idle' && <span>Auto-syncs on load + every 10 min.</span>}

      <div style={{ flex: 1 }} />

      <button
        type="button"
        onClick={onSyncNow}
        disabled={inFlight}
        style={{
          padding: '3px 10px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          background: '#fff',
          fontSize: 11,
          fontWeight: 600,
          color: inFlight ? 'var(--muted-light)' : 'var(--text-secondary)',
          cursor: inFlight ? 'wait' : 'pointer',
        }}
      >
        {inFlight ? 'Syncing…' : 'Sync now'}
      </button>
    </div>
  )
}
